import { useEffect, useState } from 'react';

/**
 * Countdown for OTP expiry and resend cooldown.
 *
 * @param {object} opts
 * @param {number|null} opts.issuedAt - epoch ms when the code was issued (null = inactive)
 * @param {number} [opts.expiresInSec=0]
 * @param {number} [opts.resendCooldownSec=0]
 */
export function useOtpCountdown({
    issuedAt = null,
    expiresInSec = 0,
    resendCooldownSec = 0,
} = {}) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!issuedAt) return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [issuedAt]);

    const active = Boolean(issuedAt);
    const expiresLeft = active && expiresInSec > 0
        ? Math.max(0, Math.ceil((issuedAt + expiresInSec * 1000 - now) / 1000))
        : 0;
    const resendLeft = active && resendCooldownSec > 0
        ? Math.max(0, Math.ceil((issuedAt + resendCooldownSec * 1000 - now) / 1000))
        : 0;

    return {
        active,
        expiresInSecLeft: expiresLeft,
        expiresLabel: formatMmSs(expiresLeft),
        expired: active && expiresInSec > 0 && expiresLeft <= 0,
        resendInSec: resendLeft,
        canResend: active && resendLeft <= 0,
    };
}

export function formatMmSs(totalSec) {
    const sec = Math.max(0, Number(totalSec) || 0);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Apply issue/resend response meta onto local OTP timer state setters.
 */
export function otpMetaFromResponse(data = {}) {
    return {
        expiresInSec: Number(data.expiresInSec) || 300,
        resendCooldownSec: Number(data.resendCooldownSec) || 60,
        maxAttempts: Number(data.maxAttempts) || 5,
        issuedAt: Date.now(),
    };
}

export default useOtpCountdown;
